import React, { useState, useEffect, useCallback } from 'react';
import { FiMenu, FiX } from 'react-icons/fi';

const NAV_LINKS = [
  { label: 'Home', href: '#hero' },
  { label: 'About', href: '#about' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Non Profit', href: '#nonprofits' },
  { label: 'People In Need', href: '#people-in-need' },
  { label: 'Contact', href: '#contact' },
];

const SECTIONS = ['hero', 'about', 'how-it-works', 'nonprofits', 'people-in-need', 'contact'];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const onScroll = () => {
      const trigger = window.scrollY + window.innerHeight * 0.35;
      let current = SECTIONS[0];
      SECTIONS.forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY;
          if (top <= trigger) current = id;
        }
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = useCallback((e, href) => {
    e.preventDefault();
    document.getElementById(href.replace('#', ''))?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  }, []);

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          background: '#1B1A28',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
        }}
      >
        <div className="flex items-center justify-between px-4 md:px-8 py-3 max-w-screen-xl mx-auto">
          {/* Logo */}
          <a href="#hero" onClick={(e) => handleNav(e, '#hero')} style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
            <img src="/logo.png" alt="Tarrant County Connect+" style={{ height: '64px', width: 'auto' }} />
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => {
              const isActive = activeSection === link.href.replace('#', '');
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNav(e, link.href)}
                  style={{
                    position: 'relative',
                    textDecoration: 'none',
                    fontFamily: 'popinsMedium',
                    fontSize: '14px',
                    transition: 'color 300ms ease',
                    color: isActive ? '#3FC4E3' : 'rgba(255,255,255,0.75)',
                    paddingBottom: '4px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#3FC4E3'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = isActive ? '#3FC4E3' : 'rgba(255,255,255,0.75)'; }}
                >
                  {link.label}
                  <span style={{
                    position: 'absolute', bottom: 0, left: 0,
                    height: '2px', borderRadius: '1px',
                    width: isActive ? '100%' : '0%',
                    background: '#3FC4E3',
                    transition: 'width 300ms ease',
                  }} />
                </a>
              );
            })}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://connect.countyconnectplus.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="nonprofit-portal-btn"
              style={{
                border: '1.5px solid #3FC4E3', color: '#3FC4E3',
                background: 'transparent', borderRadius: '8px',
                padding: '8px 20px', fontFamily: 'popinsMedium',
                fontSize: '14px', textDecoration: 'none', transition: 'background 300ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(63,196,227,0.12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              Nonprofit Portal
            </a>
            <a
              href="#people-in-need"
              onClick={(e) => handleNav(e, '#people-in-need')}
              style={{
                background: '#3FC4E3', color: '#1B1A28',
                borderRadius: '8px', padding: '8px 20px',
                fontFamily: 'popinsSemiBold', fontSize: '14px',
                textDecoration: 'none', transition: 'all 300ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#35b3d1'; e.currentTarget.style.transform = 'scale(1.03)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#3FC4E3'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              Get the App
            </a>
          </div>

          {/* Hamburger */}
          <button
            className="md:hidden cursor-pointer p-2"
            style={{ color: '#ffffff', background: 'none', border: 'none' }}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div
        className={`mobile-menu fixed inset-0 z-40 flex flex-col items-center justify-center gap-8 ${menuOpen ? 'open' : ''}`}
        style={{ background: '#1B1A28' }}
      >
        {NAV_LINKS.map((link) => (
          <a
            key={link.href}
            href={link.href}
            onClick={(e) => handleNav(e, link.href)}
            style={{ fontFamily: 'popinsMedium', fontSize: '22px', color: '#ffffff', textDecoration: 'none' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#3FC4E3'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#ffffff'; }}
          >
            {link.label}
          </a>
        ))}
        <div className="flex flex-col gap-3 mt-4 w-48">
          <a
            href="https://connect.countyconnectplus.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="nonprofit-portal-btn"
            style={{
              textAlign: 'center', border: '1.5px solid #3FC4E3', color: '#3FC4E3',
              borderRadius: '8px', padding: '12px 20px', fontFamily: 'popinsMedium',
              fontSize: '15px', textDecoration: 'none',
            }}
          >
            Nonprofit Portal
          </a>
          <a
            href="#people-in-need"
            onClick={(e) => handleNav(e, '#people-in-need')}
            style={{
              textAlign: 'center', background: '#3FC4E3', color: '#1B1A28',
              borderRadius: '8px', padding: '12px 20px', fontFamily: 'popinsSemiBold',
              fontSize: '15px', textDecoration: 'none',
            }}
          >
            Get the App
          </a>
        </div>
      </div>
    </>
  );
}
